import { EventEmitter, Injectable, Injector, OnDestroy } from '@angular/core';
import { MessageType } from 'enums/MessageType';
import { DomainModel } from 'models/generated/Distribution.Api.Models.Domain';
import { SiteModel } from 'models/generated/Distribution.Api.Models.Site';
import { CompanyAccountingSettingModel } from 'models/generated/Entities.Accounting.CompanyAccountingSetting';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { CompanyResponsibleModel } from 'models/generated/Entities.GormCrm.CompanyResponsible';
import { PlanReplacementModel } from 'models/generated/Entities.GormCrm.PlanReplacement';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { CompanyStatusSummaryModel } from 'models/generated/GormGateway.Models.CompanyStatusSummary';
import { FarPayResponseModel } from 'models/generated/GormGateway.Models.FarPayResponse';
import { SupersedableProductPlanDtoModel } from 'models/generated/GormGateway.Models.SupersedableProductPlanDto';
import { BillingInfoDtoModel } from 'models/generated/Models.BillingInfoDto';
import { Subscription } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { CompanyService } from 'services/company.service';
import { RefreshService, TrackingContext } from 'services/data-tracking';
import { DomainService } from 'services/domain.service';
import { ProductPlanService } from 'services/productplan.service';
import { PushService } from 'services/push.service';
import { CompanyActiveSettlement, LegalCaseService } from 'services/legal-case.service';
import { SiteService } from 'services/site.service';
import { SalesService } from 'services/sales.service';
import { OrderModel } from 'models/generated/Entities.GormCrm.Order';
import { LegalCaseModel } from 'models/generated/Entities.GormCrm.LegalCase';

export class DataProviderInstance {
	activeContexts: CompanyDataProviderFactory[] = [];
	lastUse = Date.now();
	getTimeLeft() { return cacheTimeout - (Date.now() - this.lastUse); }
	constructor(public readonly dataProvider: CompanyDataProvider) { }
}
const activeProviders = new Map<string, DataProviderInstance>();
const cacheTimeout = 1000 * 60;

@Injectable()
export class CompanyDataProviderFactory implements OnDestroy {
	constructor(private readonly injector: Injector) { }

	ngOnDestroy() { this.removeInstanceReference(); }

	private instance?: DataProviderInstance;
	private ensureInstanceForCompanyId(companyId: string) {

		if (this.instance && this.instance.dataProvider.companyId === companyId) return; // Already created for this company
		if (this.instance) throw new Error(`Company Data Provider: This context already has a reference to company ${this.instance.dataProvider.companyId}`); // If same context is attempted to used to create a provider for another company, this could cause issues with child components which have injected the same instance

		this.cleanUpInactiveProviders();
		let instance = activeProviders.get(companyId);

		if (!instance) {
			instance = new DataProviderInstance(new CompanyDataProvider(companyId, this.injector));
			activeProviders.set(companyId, instance);
		}

		instance.activeContexts.push(this);
		this.instance = instance;
	}
	private removeInstanceReference() {
		if (!this.instance) return;
		this.instance.lastUse = Date.now();
		const index = this.instance.activeContexts.indexOf(this);
		if (index >= 0) this.instance.activeContexts.splice(index, 1);
		this.instance = undefined;
	}
	private cleanUpInactiveProviders() {
		const now = Date.now();
		for (const [companyId, instance] of activeProviders) {
			if (instance.activeContexts.length == 0 && now - instance.lastUse > cacheTimeout) {
				activeProviders.delete(companyId);
				instance.dataProvider.destroy();
			}
		}
	}

	public getActiveProviders(): DataProviderInstance[] {
		return Array.from(activeProviders.values());
	}
	public getProviderForCompanyId(companyId: string) {
		if (!companyId || companyId == '00000000-0000-0000-0000-000000000000') throw new Error('Invalid Company ID');
		this.ensureInstanceForCompanyId(companyId);
		return this.getProviderForCurrentScope();
	}
	public getProviderForCurrentScope() {
		if (!this.instance) throw new Error('Company Data Provider: No instance was created before Get requested');
		return this.instance.dataProvider;
	}
}

export class CompanyDataProvider implements TrackingContext {

	private destroyEvent = new EventEmitter<void>();
	public onDestroy(next?: Function) { return this.destroyEvent.subscribe(next); }
	private billingPlanChangesEvent = new EventEmitter<{ accountingPlanId: string, productPlanId: string }>();
	public onBillingPlanChanges(next?: (evenData: { accountingPlanId: string, productPlanId: string }) => void) { return this.billingPlanChangesEvent.subscribe(next); }

	private companyService: CompanyService;
	private productPlanService: ProductPlanService;
	private accountingService: AccountingService;
	private legalCaseService: LegalCaseService;
	private siteService: SiteService;
	private domainService: DomainService;
	private salesService: SalesService;
	public refreshService: RefreshService;

	private company?: CompanyModel;
	private billingPlanSubscription: Subscription;
	public get companyName() { return this.company?.name; }

	private companyPromise?: Promise<CompanyModel>;
	private companyStatusPromise?: Promise<CompanyStatusSummaryModel>;
	private settlementPromise?: Promise<CompanyActiveSettlement>;
	private legalCasesPromise?: Promise<LegalCaseModel[]>;
	private companyAccountingSettingsPromise?: Promise<CompanyAccountingSettingModel[]>;
	private planReplacementsPromise?: Promise<PlanReplacementModel[]>;
	private productAndAccountingPlansPromise?: Promise<ProductPlanModel[]>;
	private ledgerPostsPromise?: Promise<LedgerPostModel[]>;
	private billingInfoPromise?: Promise<BillingInfoDtoModel>;
	private responsiblesPromise?: Promise<CompanyResponsibleModel[]>;
	private sitesPromise?: Promise<SiteModel[]>;
	private domainsPromise?: Promise<DomainModel[]>;
	private farPayPromise?: Promise<FarPayResponseModel>;
	private superSedableProductPlansPromise?: Promise<SupersedableProductPlanDtoModel[]>;
	private ordersPromise?: Promise<OrderModel[]>;

	private initialPreloadPromise: Promise<void>;

	constructor(public readonly companyId: string, injector: Injector) {

		this.companyService = injector.get(CompanyService);
		this.productPlanService = injector.get(ProductPlanService);
		this.accountingService = injector.get(AccountingService);
		this.legalCaseService = injector.get(LegalCaseService);
		this.siteService = injector.get(SiteService);
		this.domainService = injector.get(DomainService);
		this.salesService = injector.get(SalesService);
		this.refreshService = new RefreshService;
		this.initialPreloadPromise = this.preloadInitialData();

		const pushMessageService = injector.get(PushService);
		this.billingPlanSubscription = pushMessageService.onChanges<{ accountingPlanId: string, productPlanId: string }>(MessageType.BillingPlanChanges).subscribe(async (message) => {

			const productPlans = await this.getAllProductPlansAndAccountingPlans();
			if (!productPlans.some(p => p.id == message.data.productPlanId)) return;
			this.ledgerPostsPromise = undefined;
			this.getLedgerPosts();
			this.billingPlanChangesEvent.emit(message.data);
		
		});
	}

	public destroy() {
		this.destroyEvent.emit();
		this.billingPlanSubscription.unsubscribe();
	}

	private async preloadInitialData() {

		// Place any data that always should be preloaded here

		await Promise.all([
			this.getCompany()
		]);
	}

	public async preloadAllData() {

		// When requested, preload everything that could be useful to have cached

		await Promise.all([
			this.getAllProductPlansAndAccountingPlans(),
			this.getCompany(),
			this.getLedgerPosts(),
			this.getCompanyAccountingSettings(),
			this.getSettlement(),
			this.getPlanReplacements()
		]);

		// delay a bit before loading less "important" data

		await new Promise(r => setTimeout(r, 200));
		this.getBillingInfo();
		this.getCompanyResponsibles();
		this.getSites();
		this.getDomains();
	}

	// #region Data sources cached as promises. These can all be called to preload data, since multiple calls will never result in multiple requests

	async getCompany() {
		if (!this.companyPromise) this.companyPromise = this.companyService.getCompany(this.companyId, this).then(c => { this.company = c; return c; });
		return await this.companyPromise;
	}
	async getBillingInfo() {
		if (!this.billingInfoPromise) this.billingInfoPromise = this.companyService.getCompanyBillingInfo(this.companyId, this);
		return await this.billingInfoPromise;
	}
	async getCompanyResponsibles() {
		if (!this.responsiblesPromise) this.responsiblesPromise = this.companyService.getCompanyResponsibles(this.companyId);
		return await this.responsiblesPromise;
	}
	async getCompanyAccountingSettings() {
		if (!this.companyAccountingSettingsPromise) this.companyAccountingSettingsPromise = this.accountingService.getCompanySettings(this.companyId);
		return await this.companyAccountingSettingsPromise;
	}
	async getCompanyStatus(reloadCache?: boolean) {
		if (!this.companyStatusPromise || reloadCache === true) {
			this.companyStatusPromise = this.companyService.getCompanyStatus(this.companyId);
			if (this.legalCasesPromise) this.legalCasesPromise = undefined; // If company status is reloaded, we should also reload legal cases, since the status might have changed
		}
		return await this.companyStatusPromise;
	}
	async getSettlement() {
		if (!this.settlementPromise) this.settlementPromise = this.legalCaseService.getSettlementByCompanyId(this.companyId, this);
		return await this.settlementPromise;
	}
	async getLegalCases() {
		if (!this.legalCasesPromise) this.legalCasesPromise = this.legalCaseService.getLegalCasesByCompanyId(this.companyId);
		return await this.legalCasesPromise;
	}
	async getPlanReplacements() {
		if (!this.planReplacementsPromise) this.planReplacementsPromise = this.productPlanService.getCompanyPlanReplacements(this.companyId);
		return await this.planReplacementsPromise;
	}
	async getAllProductPlansAndAccountingPlans(reloadCache?: boolean): Promise<ProductPlanModel[]> {
		if (!this.productAndAccountingPlansPromise || reloadCache === true) this.productAndAccountingPlansPromise = this.productPlanService.getAllProductPlansAndAccountingPlansFromCompany(this.companyId, this);
		return await this.productAndAccountingPlansPromise;
	}
	async getSites(reloadCache?: boolean) { // Parameter reloads site cache, but not productPlan cache!
		if (!this.sitesPromise || reloadCache === true) this.sitesPromise = this.siteService.getSitesByProductPlanIds((await this.getAllProductPlansAndAccountingPlans()).map(p => p.id!), this);
		return await this.sitesPromise;
	}
	async getFarPayInfo() {
		if (!this.farPayPromise) this.farPayPromise = this.accountingService.getFarPayInfo(await this.getCompanyAccountingSettings());
		return await this.farPayPromise;
	}
	async getSupersedableProductPlans(reloadCache?: boolean): Promise<SupersedableProductPlanDtoModel[]> {
		if (!this.superSedableProductPlansPromise || reloadCache === true) this.superSedableProductPlansPromise = this.productPlanService.getSupersedableProductPlans(this.companyId);
		return await this.superSedableProductPlansPromise;
	}
	async getDomains(): Promise<DomainModel[]> {
		if (!this.domainsPromise) this.domainsPromise = this.domainService.getDomainsByCompanyId(this.companyId, this);
		return await this.domainsPromise;
	}
	private async getLedgerPosts(): Promise<LedgerPostModel[]> {
		if (!this.ledgerPostsPromise) {
			const productPlanIds = (await this.getAllProductPlansAndAccountingPlans()).map(pp => pp.id!);
			if (productPlanIds.length == 0) return [];
			this.ledgerPostsPromise = this.accountingService.getLedgerPostsByProductPlanIds(productPlanIds);
		}
		return await this.ledgerPostsPromise;
	}

	// #endregion

	async getOrders(): Promise<OrderModel[]> {
		if (!this.ordersPromise) this.ordersPromise = this.salesService.getOrders({ companyId: this.companyId }, this);
		return await this.ordersPromise;
	}

	async getProductPlanWithAccountingPlans(productPlanId: string): Promise<ProductPlanModel> {
		const cachedProductPlans = await this.getAllProductPlansAndAccountingPlans();
		let productPlan = cachedProductPlans.find(pp => pp.id == productPlanId);

		if (productPlan) return productPlan;

		productPlan = await this.productPlanService.getProductPlan(productPlanId);
		if (!productPlan) throw new Error(`Product plan with id ${productPlanId} not found in current company scope`);

		const accountingPlans = await this.accountingService.getCurrentAccountingPlansFromProductPlanIds([productPlanId]);
		productPlan.accountingPlans = accountingPlans;
		cachedProductPlans.push(productPlan);
		return productPlan;
	}

	async getLedgerPostsByProductPlanIds(productPlanIds: string[], historicalDate?: Date): Promise<LedgerPostModel[]> {
		// Don't cache ledger post *history*, as it's only displayed by specific requests, and we don't expect it to be a common performance bottleneck
		if (historicalDate) return await this.accountingService.getLedgerPostsByProductPlanIds(productPlanIds, historicalDate);

		const accountingPlanIds = (await this.getAllProductPlansAndAccountingPlans()).filter(pp => productPlanIds.some(id => id == pp.id)).flatMap(pp => pp.accountingPlans)?.map(ap => ap.id);
		if (!accountingPlanIds) return [];
		return (await this.getLedgerPosts()).filter(post => accountingPlanIds!.some(id => id == post.accountingPlanId)) ?? [];
	}

	async planIsPartOfActiveSettlement(productPlan: ProductPlanModel): Promise<boolean> {
		return (await this.getSettlement()).activeSettlement?.settlementProductPlans.some(spp => spp.productPlanId == productPlan.id) ?? false;
	}

	async planIsReplaced(productPlan: ProductPlanModel): Promise<boolean> {
		return await this.getPlanReplacedStatus(productPlan, true);
	}

	async planIsGoingToBeReplaced(productPlan: ProductPlanModel): Promise<boolean> {
		if (await this.getPlanReplacedStatus(productPlan, false)) return true;
		
		// If productplan isn't represented by a plan replacement, it might be included as part of an discontinuation on an order that hasn't yet been approved or cancelled
		const orders = await this.getOrders();
		return orders.some(o => o.orderCancellations.length == 0 && !o.approvedDate && o.orderProductPlanDiscontinuations.some(opd => opd.productPlanId == productPlan.id));
	}

	private async getPlanReplacedStatus(productPlan: ProductPlanModel, planReplacementDelivered: boolean): Promise<boolean> {
		let planReplacements = await this.getPlanReplacements();
		if (planReplacementDelivered)
			planReplacements = planReplacements.filter(pr => pr.delivered)
		else
			planReplacements = planReplacements.filter(pr => !pr.delivered)
		return planReplacements.some(r => r.planReplacementOldPlans.some(s => s.productPlanId == productPlan.id));
	}
	
	async getProductPlansByTag(...tags: string[]) {
		const plans = await this.getAllProductPlansAndAccountingPlans();
		let filteredPlans: ProductPlanModel[] = [];
		for (const tag of tags) {
			filteredPlans = filteredPlans.concat(this.productPlanService.filterProductPlansByTag(plans, tag));
		}
		return [...new Set(filteredPlans)]; // Ensure distinct values, if some plans match on multiple tags
	}
}
